<!--Copy right - © 2024 tecera gmbh-->
<script>
    import {
        is_authenticated,
        check_if_authenticated,
        user_permissions,
        rerout_user,
    } from "../scripts/auth.js";
    import { onDestroy, onMount } from "svelte";
    import {
        all_hymos,
        selected_device,
        map_center,
        hymo_alarms_triggered_new,
        hymo_alarms_triggered_old,
        modal_mode,
        is_modal_open,
        free_hymos,
        current_device_data,
        last_meta_data,
        hymo_data_end_date,
    } from "../stores.js";
    import {
        ALARM_REFRESH_INTERVALL_IN_SECONDS,
        DATA_REFRESH_INTERVALL_IN_SECONDS,
        ENABLE_LIVE_RELOADING,
    } from "../config.js";
    import NavBar from "../components/NavBar.svelte";
    import Footer from "../components/Footer.svelte";
    import MessgroesseButton from "../components/buttons/MessgroesseButton.svelte";
    import SwitchButton from "../components/buttons/SwitchButton.svelte";
    import SignalDisplay from "../components/SignalDisplay.svelte";
    import StreetMap from "../components/StreetMap.svelte";
    import HymoGraph from "../components/HymoGraph.svelte";
    import NeuesGeraetButton from "../components/buttons/NeuesGeraetButton.svelte";
    import NeuesGeraetModal from "../components/modals/NeuesGeraetModal.svelte";
    import DeviceButton from "../components/buttons/DeviceButton.svelte";
    import Meldungen from "../components/Meldungen.svelte";
    import MeldungenEntry from "../components/MeldungenEntry.svelte";
    import DeviceSearchField from "../components/DeviceSearchField.svelte";
    import GrenzwerteDeviceButton from "../components/buttons/GrenzwerteDeviceButton.svelte";
    import AlarmAuswahl from "../components/AlarmAuswahl.svelte";
    import AlarmKreisModal from "../components/modals/AlarmKreisModal.svelte";
    import DatePicker from "../components/DatePicker.svelte";
    import SegmentControl2 from "../components/SegmentControl2.svelte";
    import DataTabelleNew from "../components/DataTabelleNew.svelte";
    import { fade } from "svelte/transition";

    import {
        get_free_hymos,
        get_hymo_configs,
        get_alarms_current_hymo,
        get_alarms_old_hymo,
        get_hymo_data,
        create_extended_channel_list,
    } from "../scripts/data.js";

    let show_loaders = false;
    let show_old_alarms = false;
    let device_search_input = "";
    let init_alarm_fetch = true;
    let alarm_fetch_interval, data_fetch_interval;
    let is_alarmierungskreis_modal_open = false;
    var end_date = moment(); // Now
    var start_date = moment().subtract(5, "days"); // 5 days ago
    let show_data_table = false;
    let chosen_segment = "Sensoren";
    let chart_data;
    let is_mounted = false;
    let data_promise;

    onMount(async () => {
        is_mounted = true;
        $selected_device = null; // need to set this to null incase user comes from different tab
        await check_if_authenticated(); // waiting for auth before proceeding
        data_promise = new Promise((resolve) => {
            setTimeout(() => {
                resolve("Fake data for loader");
            }, 500);
        });
        if (
            !$user_permissions["access_hymo"] ||
            $user_permissions["access_hymo"] != 1
        ) {
            rerout_user();
            return;
        }
        fetch_hymo_configs(true);
        get_free_hymos();
        get_alarms();
        if (ENABLE_LIVE_RELOADING) {
            alarm_fetch_interval = setInterval(
                get_alarms,
                ALARM_REFRESH_INTERVALL_IN_SECONDS * 1000,
            );
            data_fetch_interval = setInterval(
                refresh_hymo_data,
                DATA_REFRESH_INTERVALL_IN_SECONDS * 1000,
            );
        }
    });

    $: {
        $hymo_data_end_date = end_date;
    }

    async function fetch_hymo_configs(reset_map=false) {
        show_loaders = true;
        await get_hymo_configs("hymo");
        if (reset_map) {
            map_center.set([52.022146, 10.566208]);
        } else {
            $selected_device = $all_hymos[0];
        }
        show_loaders = false;
    }

    async function get_alarms() {
        await get_alarms_current_hymo();
        init_alarm_fetch = false;
        await get_alarms_old_hymo();
    }

    async function refresh_hymo_data() {
        if (
            moment().diff(end_date) / 1000 <
            DATA_REFRESH_INTERVALL_IN_SECONDS * 4
        ) {
            end_date = moment();
        }
        if (
            $selected_device &&
            $all_hymos.some((hymo) => hymo.id === $selected_device.id)
        ) {
            // added .some check so wrong device type can't be selected
            get_hymo_data($selected_device.id, start_date, end_date);
        }
    }

    onDestroy(() => {
        clearInterval(alarm_fetch_interval);
        clearInterval(data_fetch_interval);
        $all_hymos = [];
        $hymo_alarms_triggered_new = [];
        $hymo_alarms_triggered_old = [];
        $free_hymos = [];
        $selected_device = null;
        $current_device_data = [];
        $last_meta_data = null;
    });

    // reactive centering of the map on the selected device
    $: {
        if (
            $selected_device &&
            $all_hymos.some((hymo) => hymo.id === $selected_device.id)
        ) {
            select_device();
        }
    }

    function select_device(){
            // added .some check so wrong device type can't be selected
            reset_data();
            data_promise = get_hymo_data(
                $selected_device.id,
                start_date,
                end_date,
            );
            create_extended_channel_list();
            map_center.set([
                $selected_device.latitude,
                $selected_device.longitude,
            ]);
        }
        
    // TODO: Sort all_alarms by some criteria

    function reset_data() {
        $current_device_data = [];
        $last_meta_data = null;
    }

    $: {
        if ($selected_device && start_date && end_date) {
            refresh_hymo_data();
        }
    }
</script>

<svelte:head>
    <title>Mapview - Gewässerüberwachung</title>
</svelte:head>

{#if $is_authenticated && $user_permissions["access_hymo"] == 1}
    <div class="container-fluid">
        <div class="row">
            <!-- Main Content -->
            <main class="col-12 ml-sm-auto px-md-4">
                <NavBar headline="Gewässerüberwachung" />
                <div class="row">
                    <!-- Linke Seite -->
                    <div
                        class="col-12 col-xl-9 col-lg-8 col-md-7 content_main"
                        style="display:flex;flex-direction:column;justify-content:flex-start;"
                    >
                        <div class="row" style="flex: 0 1 5%;">
                            <!-- Buttons -->
                            <div
                                class="col-9 col-lg-9 col-md-8 pl-3 pt-3 pr-0"
                                style="align-items: flex-start;justify-content: flex-start;display:flex;flex: 1 1 60%;"
                            >
                                <div
                                    class="d-md-flex d-inline justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2"
                                >
                                    <div class="btn-toolbar pb-1">
                                        <NeuesGeraetButton
                                            label={"Neues Gerät"}
                                            func={() => {
                                                $modal_mode = "create";
                                                $is_modal_open = true;
                                            }}
                                            id="hymo_new_device_button"
                                        />
                                        <DatePicker
                                            bind:start_date
                                            bind:end_date
                                        />
                                        <MessgroesseButton
                                            multiple_devices={false}
                                            bind:chart_data
                                        />
                                        <!-- <SwitchButton
                                            bind:toggle_state={show_data_table}
                                            label0="Tabelle"
                                            label1="Diagramm"
                                            icon_0="fa-table"
                                            icon_1="fa-chart-line"
                                        /> -->
                                    </div>
                                </div>
                            </div>

                            <!-- Letzter Sendezeitraum und Signalstaerke -->
                            <div
                                class="col-3 col-lg-3 col-md-4 p-0 ml-0 pr-2 pt-3"
                                style="align-items: flex-end;justify-content: flex-end;display:flex;flex: 1 0 150px;min-width:fit-content;"
                            >
                                <SignalDisplay />
                            </div>
                        </div>

                        {#if !show_data_table}
                            <!-- Map -->
                            <div class="row" style="flex: 5 5 30%;">
                                <div class="col-12 full">
                                    <StreetMap
                                        devices={all_hymos}
                                        alarms_triggered={hymo_alarms_triggered_new}
                                        multiple_devices={false}
                                    />
                                </div>
                            </div>

                            <!-- Diagramm -->
                            <div class="row pt-4" style="flex: 1 1 30%;">
                                {#await data_promise}
                                    <div class="col-12 full">
                                        <div
                                            id="loader_devices"
                                            class="loader"
                                        />
                                    </div>
                                {:then}
                                    {#if $current_device_data.length > 0}
                                        <div class="col-12 full">
                                            <HymoGraph />
                                        </div>
                                    {:else}
                                        <div
                                            class="col-12 full center-flex-box"
                                        >
                                            <p class="h1 p-kachel-2">
                                                Keine Daten im angegebenen
                                                Zeitraum verfügbar
                                            </p>
                                        </div>
                                    {/if}
                                {/await}
                            </div>
                        {:else}
                            <div
                                class="row"
                                style="flex: 1 1 10%;overflow:hidden;"
                                in:fade={{ delay: 0, duration: 200 }}
                            >
                                <DataTabelleNew {is_mounted} bind:chart_data />
                            </div>
                        {/if}
                    </div>

                    <!-- Rechte Seite -->
                    {#if chosen_segment === "Sensoren"}
                        <div
                            class="col-12 col-xl-3 col-lg-4 col-md-5 content_main content_main_side"
                            style="display:flex;flex-direction:column;justify-content:flex-start;"
                        >
                            <!-- HyMo Menü -->
                            <div
                                class="row pt-4"
                                style="flex: 1 1 50%;overflow:hidden;"
                            >
                                <div class="col-12 full">
                                    <div class="display_box side-menu">
                                        <SegmentControl2
                                            bind:chosen_segment
                                            segments={["Sensoren", "Grenzwerte"]}
                                        />
                                        <p class="p-kachel">Sensoren</p>

                                        <!-- Suchfeld -->
                                        <DeviceSearchField
                                            bind:device_search_input
                                        />

                                        <div class="scroll_window">
                                            <!-- Darstellungsraum der HyMos -->
                                            {#if show_loaders}
                                                <div
                                                    id="loader_devices"
                                                    class="loader"
                                                />
                                            {:else}
                                                {#each $all_hymos as device_config (device_config.id)}
                                                    {#if !device_config.name || device_config.name
                                                            .toLowerCase()
                                                            .includes(device_search_input.toLowerCase()) || device_config.dev_eui
                                                            .toLowerCase()
                                                            .includes(device_search_input.toLowerCase())}
                                                        <DeviceButton
                                                            {device_config}
                                                            multiple_devices={false}
                                                        />
                                                    {/if}
                                                {/each}
                                            {/if}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Meldungen -->
                            <div
                                class="row pt-4"
                                style="flex: 1 1 50%;overflow:hidden;"
                            >
                                <div class="col-12 full">
                                    <Meldungen bind:show_old_alarms>
                                        {#if init_alarm_fetch}
                                            <div
                                                id="loader_devices"
                                                class="loader"
                                            />
                                        {:else if show_old_alarms}
                                            {#each $hymo_alarms_triggered_old as alarm_triggered (alarm_triggered.id)}
                                                <MeldungenEntry
                                                    {alarm_triggered}
                                                    {show_old_alarms}
                                                    type="hymo"
                                                    alarm_list={hymo_alarms_triggered_old}
                                                />
                                            {/each}
                                        {:else}
                                            {#each $hymo_alarms_triggered_new as alarm_triggered (alarm_triggered.id)}
                                                <MeldungenEntry
                                                    {alarm_triggered}
                                                    {show_old_alarms}
                                                    type="hymo"
                                                    alarm_list={hymo_alarms_triggered_new}
                                                />
                                            {/each}
                                        {/if}
                                    </Meldungen>
                                </div>
                            </div>
                        </div>
                    {:else}
                        <!-- Grenzwerte Menu -->
                        <div
                            class="col-12 col-xl-3 col-lg-4 col-md-5 content_main content_main_side"
                            style="display:flex;flex-direction:column;justify-content:flex-start;flex: 2 2 325px;"
                        >
                            <div
                                class="row pt-4"
                                style="flex: 1 1 50%;overflow:hidden;"
                            >
                                <div class="col-12 full">
                                    <div
                                        class="display_box side-menu side-menu-scroll"
                                    >
                                        <SegmentControl2
                                            bind:chosen_segment
                                            segments={["Sensoren", "Grenzwerte"]}
                                        />
                                        <p class="p-kachel">Grenzwerte</p>

                                        <GrenzwerteDeviceButton
                                            device_list={all_hymos}
                                        />

                                        <div class="input-group mb-2 mt-4">
                                            <button
                                                id="show_alarmierungskreis"
                                                class="btn btn-primary btn-block"
                                                on:click={() => {
                                                    is_alarmierungskreis_modal_open = true;
                                                }}
                                                disabled={$selected_device ? false : true}
                                            >
                                                Alarmierungskreis
                                            </button>
                                        </div>
                                        <AlarmAuswahl />
                                    </div>
                                </div>
                            </div>
                        </div>
                    {/if}
                </div>
            </main>
        </div>
    </div>

    <NeuesGeraetModal bind:is_open={$is_modal_open} device_type_name="Pegelstandsensor"/>
    <AlarmKreisModal bind:is_open={is_alarmierungskreis_modal_open} />
    <Footer />
{/if}
